import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AppLayout } from "./DashboardPage";
import { useAuth } from "../App";
import WallDisplayPage from "./homeos/WallDisplayPage";
import {
  Sun,
  Mic, Moon,
  Lightbulb, ChevronRight, Sparkles, Plus,
  Sunrise, Sunset, CloudSun, SmilePlus,
  ArrowRightCircle,
  LayoutDashboard, MonitorPlay, Shield,
} from "lucide-react";
import BetaFeedbackButton from "../components/BetaFeedbackButton";
import { HomeOSSubNav } from "./homeos/HomeOSSubNav";
import { FamilyMemberCardCompact } from "./homeos/FamilyMemberCard";
import { CalmModeModal, CalmModeStatusPill } from "./homeos/CalmModeModal";
import { useCalmMode } from "./homeos/CalmModeContext";
import { WeeklyResetCard, WeeklyResetModal, useWeeklyReset } from "./homeos/WeeklyReset";
import { FamilyMemberEditModal } from "./homeos/components/FamilyMemberEditModal";
import { QuickCaptureModal } from "./homeos/components/QuickCaptureModal";
import { HomeOSSectionBento } from "./homeos/components/HomeOSSectionBento";
import { MemoryOfTheDayCard } from "./homeos/components/MemoryOfTheDayCard";
import { useHomeOSState } from "./homeos/state/HomeOSStateProvider";
import {
  todayTimeline as seedTimeline,
} from "./homeos/data/homeOsMockData";
import {
  HOME_OS_MODULES,
  CATEGORY_TO_MODULE,
  moduleForCategory,
} from "./homeos/data/homeOsModuleConfig";
import { MOOD_ZONES } from "./homeos/config/homeOsTheme";
import { detectMoodCleaningInsights } from "./homeos/utils/moodAwareDelegation";
import { getSwapInsight, getPendingHelpItems } from "./homeos/utils/taskSwapUtils";
import {
  getWeekRange, getMoodTrendSummary, getCleaningSummary, getSwapSummary,
} from "./homeos/utils/weeklyReportUtils";
import { SwapTaskModal } from "./homeos/components/SwapTaskModal";
import { HomeOSAvatar } from "./homeos/components/HomeOSAvatar";
import { HeartHandshake, CalendarHeart } from "lucide-react";
import { DailyFamilyScheduleBar } from "./homeos/components/DailyFamilyScheduleBar";
import { RenewalRadar } from "./homeos/components/RenewalRadar";
import { RenewalPrepCard } from "./homeos/components/RenewalPrepCard";
import { getSuppliesSummary, getLowStockItems, getUseSoonItems } from "./homeos/utils/suppliesUtils";
import { getDocumentSummary, getDocumentsDueSoon, getDocumentsNeedingAction } from "./homeos/utils/documentUtils";

/* ════════════════════════════════════════════════════════════════════════
   MOTHERSHIP DASHBOARD — bright, family-friendly command centre.
   ════════════════════════════════════════════════════════════════════════ */

const TIME_GREETINGS = [
  { from: 0,  to: 4,  text: "Late night",   icon: Moon    },
  { from: 5,  to: 11, text: "Good morning", icon: Sunrise },
  { from: 12, to: 16, text: "Good afternoon", icon: CloudSun },
  { from: 17, to: 20, text: "Good evening", icon: Sunset  },
  { from: 21, to: 23, text: "Winding down", icon: Moon    },
];

function getGreeting(hour) {
  return TIME_GREETINGS.find(g => hour >= g.from && hour <= g.to) || TIME_GREETINGS[1];
}

const STATUS_PILL = {
  done:         { label: "Done",         cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300" },
  upcoming:     { label: "Upcoming",     cls: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300" },
  needs_action: { label: "Needs action", cls: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300" },
  ai_suggested: { label: "Ara suggested", cls: "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300" },
};

function StatusPill({ status }) {
  const cfg = STATUS_PILL[status] || STATUS_PILL.upcoming;
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-bold ${cfg.cls}`} data-testid={`status-pill-${status}`}>
      {cfg.label}
    </span>
  );
}

export default function HomeOSPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const viewMode = searchParams.get("mode") === "wall" ? "wall" : "command";
  const setMode = (m) => {
    const next = new URLSearchParams(searchParams);
    if (m === "wall") next.set("mode", "wall");
    else next.delete("mode");
    setSearchParams(next, { replace: true });
  };
  const { members: householdMembers, tasks, settings, moodCheckins, cleaningTasks, rooms, suppliesItems, documents } = useHomeOSState();
  const { isCalm } = useCalmMode();
  const weeklyReset = useWeeklyReset();

  const [items, setItems] = useState(seedTimeline);
  const [calmModalOpen, setCalmModalOpen] = useState(false);
  const [editingMemberId, setEditingMemberId] = useState(null);
  const [quickCaptureOpen, setQuickCaptureOpen] = useState(false);
  const [swapTask, setSwapTask] = useState(null);

  const now = new Date();
  const greeting = getGreeting(now.getHours());
  const dateLabel = now.toLocaleDateString("en-NZ", { weekday: "long", month: "long", day: "numeric" });

  const toggleItem = (id) => {
    setItems(prev => prev.map(item =>
      item.id === id ? { ...item, status: item.status === "done" ? "upcoming" : "done" } : item
    ));
  };

  /* Module status counts derived from real tasks + cleaning + mood. */
  const moduleStatus = useMemo(() => {
    const counts = {};
    (tasks || []).forEach(t => {
      const id = CATEGORY_TO_MODULE[t.category] || "today";
      if (!counts[id]) counts[id] = { total: 0, open: 0 };
      counts[id].total++;
      if (t.status !== "Done") counts[id].open++;
    });
    const openCleaning = (cleaningTasks || []).filter(t => t.status !== "Done").length;
    counts.cleaning = { total: (cleaningTasks || []).length, open: openCleaning };
    return counts;
  }, [tasks, cleaningTasks]);

  /* Today's mood check-ins */
  const today = new Date().toISOString().slice(0, 10);
  const todayMood = useMemo(() => {
    return (moodCheckins || []).filter(c => (c.createdAt || "").slice(0, 10) === today);
  }, [moodCheckins, today]);

  /* Supplies summary — fuels the supplies tile + ARA insights. */
  const suppliesSummary = useMemo(() => getSuppliesSummary(suppliesItems || []), [suppliesItems]);
  const lowSupplyNames = useMemo(() => getLowStockItems(suppliesItems || []).slice(0, 3).map(s => s.name), [suppliesItems]);
  const useSoonNames = useMemo(() => getUseSoonItems(suppliesItems || []).slice(0, 3).map(s => s.name), [suppliesItems]);

  /* Documents summary — fuels the documents tile + ARA insights. */
  const documentsSummary = useMemo(() => getDocumentSummary(documents || []), [documents]);
  const documentsDueSoon = useMemo(() => getDocumentsDueSoon(documents || [], 14).slice(0, 3), [documents]);
  const documentsNeedsAction = useMemo(() => getDocumentsNeedingAction(documents || []).slice(0, 3), [documents]);

  const moodByMember = useMemo(() => {
    const map = new Map();
    todayMood.forEach(c => { if (!map.has(c.memberId)) map.set(c.memberId, c); });
    return map;
  }, [todayMood]);

  /* Mood pulse — % of family with check-ins, primary zone counts */
  const moodPulse = useMemo(() => {
    const total = householdMembers.filter(m => m.ageGroup !== "Baby" && m.ageGroup !== "Pet").length;
    const checkedIn = todayMood.length;
    const zoneCounts = todayMood.reduce((acc, c) => { acc[c.zone] = (acc[c.zone] || 0) + 1; return acc; }, {});
    let summary = "Calm and ready";
    if (zoneCounts.red >= 1) summary = "Some big feelings around";
    else if (zoneCounts.blue >= 1) summary = "A few low-energy folks";
    else if (zoneCounts.yellow >= 2) summary = "High energy in the house";
    else if (checkedIn === 0) summary = "No check-ins yet";
    return { total, checkedIn, zoneCounts, summary };
  }, [todayMood, householdMembers]);

  /* Smart tile value resolver — retained for ARA noticed insights derivation. */
  // (status tile values now live in osSections below)

  /* ARA noticed — derived suggestions based on real state, incl. mood × cleaning + kindness swaps. */
  const araNoticed = useMemo(() => {
    const list = [];
    const moodInsights = detectMoodCleaningInsights({ moodCheckins, members: householdMembers, cleaningTasks });
    list.push(...moodInsights);

    const swapInsight = getSwapInsight({ tasks, cleaningTasks, members: householdMembers });
    if (swapInsight) list.unshift(swapInsight); // kindness moments first

    if (moduleStatus.cleaning?.open > 8 && !list.some(i => i.id === "cleaning-rebalance"))
      list.push({ id: "cleaning-uneven", text: "Cleaning load is high this week. Try the Delegate Cleaning plan.", action: { label: "Open Cleaning", route: "/home-os/cleaning" } });
    if ((moduleStatus.money?.open || 0) >= 1)
      list.push({ id: "money-bills", text: "There are open bills this week — review before they're due.", action: { label: "Open Money", route: "/home-os/money" } });

    /* Supplies insights — surface low / out / use-soon items naturally. */
    if (suppliesSummary.needsBuying > 0) {
      const names = lowSupplyNames.join(", ");
      list.push({
        id: "supplies-low",
        text: names
          ? `${names} ${lowSupplyNames.length === 1 ? "is" : "are"} running low.`
          : `${suppliesSummary.needsBuying} household items are running low.`,
        action: { label: "Open Supplies", route: "/home-os/supplies" },
      });
    }
    if (suppliesSummary.useSoonCount > 0) {
      const names = useSoonNames.join(", ");
      list.push({
        id: "supplies-use-soon",
        text: names ? `Use soon: ${names}.` : `${suppliesSummary.useSoonCount} items to use soon.`,
        action: { label: "Open Supplies", route: "/home-os/supplies" },
      });
    }

    /* Documents insights — surface upcoming due dates + needs action. */
    if (documentsDueSoon.length > 0) {
      const first = documentsDueSoon[0];
      const ref = first.dueDate || first.renewalDate || first.reminderDate;
      list.push({
        id: "documents-due",
        text: `${first.title}${ref ? ` is due ${ref}` : ""}.`,
        action: { label: "Open Documents", route: "/home-os/documents" },
      });
    }
    if (documentsNeedsAction.length > 0 && documentsSummary.needsAction >= 2) {
      list.push({
        id: "documents-needs",
        text: `${documentsSummary.needsAction} captured documents are still in the inbox.`,
        action: { label: "Open Documents", route: "/home-os/documents" },
      });
    }

    if (list.length === 0)
      list.push({ id: "calm", text: "The house is looking calm and on track. Nice work.", action: null });
    return list.slice(0, 5);
  }, [moduleStatus, moodCheckins, householdMembers, cleaningTasks, tasks, suppliesSummary, lowSupplyNames, useSoonNames, documentsSummary, documentsDueSoon, documentsNeedsAction]);

  const familyPulse = useMemo(() => {
    const open = (tasks || []).filter(t => t.status === "Needs action").length;
    if (open >= 3) return `${open} things need attention before dinner.`;
    if (moodPulse.zoneCounts.red >= 1) return "Today is looking emotional — keep it gentle.";
    if (moodPulse.checkedIn === moodPulse.total && moodPulse.total > 0) return "Everyone checked in. Family is in sync.";
    return "Today is looking busy but manageable.";
  }, [tasks, moodPulse]);

  /* Pending Help / Swap opportunities — surface kindness, not pressure. */
  const helpPayload = useMemo(
    () => getPendingHelpItems({ tasks, cleaningTasks, members: householdMembers, moodCheckins }, 3),
    [tasks, cleaningTasks, householdMembers, moodCheckins]
  );

  /* Weekly report preview — three small live counts for the compact card. */
  const weeklyPreview = useMemo(() => {
    const week = getWeekRange();
    const reportState = { tasks, cleaningTasks, members: householdMembers, moodCheckins, rooms };
    const moodS = getMoodTrendSummary(reportState, week);
    const cleanS = getCleaningSummary(reportState, week);
    const swapS = getSwapSummary(reportState, week);
    return { moodCount: moodS.total, openCleaning: cleanS.openCount, swapCount: swapS.count, weekLabel: week.label };
  }, [tasks, cleaningTasks, householdMembers, moodCheckins, rooms]);

  /* ──────────────────────────────────────────────────────────────────
     HOME OS section bento — section descriptors driven by live data.
     Each entry pairs a section identity (theme/colour) with the
     existing route that's already safe to open. No new routes.
     ────────────────────────────────────────────────────────────── */
  const osSections = useMemo(() => {
    const openMoney = moduleStatus.money?.open || 0;
    const openCalendar = moduleStatus.calendar?.open || 0;
    const openCleaning = moduleStatus.cleaning?.open || 0;
    const openChores = moduleStatus.chores?.open || 0;
    const openHealth = moduleStatus.health?.open || 0;

    return [
      {
        sectionId: "familyplan",
        moduleKey: "familyplan",
        label: "Family Plan",
        description: "Goals, budgets and this cycle.",
        status: "Open the plan",
        cta: "Open",
      },
      {
        sectionId: "financial",
        moduleKey: "money",
        label: "Financials",
        description: "Money, goals and cashflow.",
        status: openMoney > 0 ? `${openMoney} bill${openMoney === 1 ? "" : "s"} open` : "Plan on track",
        cta: openMoney > 0 ? "Review" : "Open",
      },
      {
        sectionId: "schedules",
        moduleKey: "calendar",
        label: "Schedules",
        description: "Family rhythm and appointments.",
        status: openCalendar > 0 ? `${openCalendar} event${openCalendar === 1 ? "" : "s"} ahead` : "Calm week",
        cta: "Open",
      },
      {
        sectionId: "meals",
        moduleKey: "meals",
        label: "Meals",
        description: "Plan kai, recipes and shopping.",
        status: "Dinner tonight",
        cta: "Plan",
      },
      {
        sectionId: "cleaning",
        moduleKey: "cleaning",
        label: "Cleaning",
        description: "Rooms, resets and jobs.",
        status:
          openCleaning + openChores > 0
            ? `${openCleaning + openChores} job${openCleaning + openChores === 1 ? "" : "s"} today`
            : "All reset",
        cta: openCleaning + openChores > 0 ? "Check" : "Open",
      },
      {
        sectionId: "maintenance",
        moduleKey: "maintenance",
        label: "Maintenance",
        description: "House repairs, jobs and upkeep.",
        status: "Open jobs list",
        cta: "Open",
      },
      {
        sectionId: "health",
        moduleKey: "health",
        label: "Health",
        description: "Care notes, appointments and routines.",
        status: openHealth > 0 ? `${openHealth} coming up` : "Nothing urgent",
        cta: "Open",
      },
      {
        sectionId: "documents",
        moduleKey: "documents",
        label: "Documents",
        description: "Bills, forms and renewals.",
        status:
          documentsSummary.needsAction > 0
            ? `${documentsSummary.needsAction} need review`
            : documentsSummary.dueSoon > 0
              ? `${documentsSummary.dueSoon} due soon`
              : "All filed",
        cta: documentsSummary.needsAction > 0 ? "Review" : "Open",
      },
      {
        sectionId: "supplies",
        moduleKey: "supplies",
        label: "Inventory",
        description: "Pantry and household stock.",
        status:
          suppliesSummary.needsBuying > 0
            ? `${suppliesSummary.needsBuying} running low`
            : "All stocked",
        cta: suppliesSummary.needsBuying > 0 ? "Check" : "Open",
      },
      {
        sectionId: "contacts",
        moduleKey: "contacts",
        label: "Contacts",
        description: "Family members and household contacts.",
        status: `${householdMembers.length} member${householdMembers.length === 1 ? "" : "s"}`,
        cta: "Open",
      },
      {
        sectionId: "holidays",
        moduleKey: "calendar",
        label: "Holidays & Events",
        description: "Birthdays, gifts and traditions.",
        status: "Plan ahead",
        cta: "Plan",
      },
      {
        sectionId: "memories",
        moduleKey: "memories",
        label: "Memories & Media",
        description: "Photos, videos and family albums.",
        status: "Family library",
        cta: "Open library",
      },
      {
        sectionId: "mood",
        moduleKey: "mood",
        label: "Grateful Tree",
        description: "Daily check-ins and gratitude.",
        status:
          moodPulse.checkedIn === 0
            ? "No check-ins yet"
            : `${moodPulse.checkedIn} of ${moodPulse.total} checked in`,
        cta: moodPulse.checkedIn === 0 ? "Check in" : "Open",
      },
    ];
  }, [moduleStatus, documentsSummary, suppliesSummary, moodPulse, householdMembers]);

  const doneCount = items.filter(i => i.status === "done").length;

  /* ─── Wall Display mode — render the wall view inside Today's Overview.
        The full-bleed WallDisplayPage is reused so we don't duplicate
        visuals; a small "Command View" pill returns the user. ─── */
  if (viewMode === "wall") {
    return (
      <div data-testid="homeos-wall-mode">
        <button
          onClick={() => setMode("command")}
          className="fixed top-3 right-3 z-50 inline-flex items-center gap-1.5 bg-white/95 hover:bg-white text-slate-800 text-[12px] font-extrabold px-3 py-1.5 rounded-full shadow-md border border-white/40"
          data-testid="homeos-mode-command-btn"
        >
          <LayoutDashboard className="w-3.5 h-3.5" strokeWidth={2.5} />
          Command View
        </button>
        <WallDisplayPage />
      </div>
    );
  }

  return (
    <AppLayout>
      <div className="font-home min-w-0 max-w-full" data-testid="homeos-dashboard">

        {/* ════ HERO — Today's Overview ════ */}
        <div className="px-5 sm:px-8 pt-6 pb-3">
          <div
            className="rounded-[28px] text-white p-6 sm:p-8 shadow-[0_8px_30px_rgba(13,148,136,0.25)] overflow-hidden relative"
            style={{ background: "linear-gradient(135deg, #0D9488 0%, #14B8A6 35%, #38BDF8 75%, #93C5FD 100%)" }}
          >
            {/* Decorative halos in section palette */}
            <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-white/15 blur-3xl" aria-hidden />
            <div className="absolute -bottom-20 -left-12 w-64 h-64 rounded-full bg-sky-200/25 blur-3xl" aria-hidden />

            <div className="relative flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0">
                <div className="inline-flex items-center gap-2 text-white/90 text-[11px] font-extrabold uppercase tracking-[0.18em] bg-white/15 backdrop-blur-sm border border-white/25 px-3 py-1 rounded-full">
                  <greeting.icon className="w-3.5 h-3.5" strokeWidth={2.5} />
                  <span data-testid="homeos-greeting">{greeting.text}</span>
                  <span className="opacity-50">·</span>
                  <span className="font-bold">{dateLabel}</span>
                </div>
                <h1 className="text-[34px] sm:text-5xl font-extrabold mt-3 tracking-tight leading-[1.05]" data-testid="homeos-title">
                  Today's Overview
                </h1>
                <p className="text-base sm:text-lg text-white/90 mt-2 max-w-xl leading-relaxed font-medium" data-testid="homeos-subtitle">
                  The family's day, priorities and household signals in one calm view.
                </p>
                <p className="mt-3 inline-flex items-center gap-2 text-[13px] sm:text-sm font-bold text-white bg-white/15 backdrop-blur-sm border border-white/25 px-3 py-1.5 rounded-full" data-testid="homeos-family-pulse">
                  <Sparkles className="w-3.5 h-3.5" strokeWidth={2.5} />
                  Kia ora, {(user?.name || settings.householdName || "whānau").split(" ")[0]} · {familyPulse}
                </p>
                <p className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-semibold tracking-wide text-white/80" data-testid="homeos-prepared-by-ara">
                  <Shield className="w-3 h-3" strokeWidth={2.25} style={{ color: '#FDE68A' }} />
                  <span>Prepared by ARA · Updated {new Date().toLocaleTimeString('en-NZ', { hour: 'numeric', minute: '2-digit' })}</span>
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0 pt-1 flex-wrap justify-end">
                {/* Command / Wall mode toggle */}
                <div className="inline-flex items-center bg-white/15 backdrop-blur-sm border border-white/25 rounded-full p-0.5" role="tablist" data-testid="homeos-mode-toggle">
                  <button
                    onClick={() => setMode("command")}
                    className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-extrabold transition-colors ${viewMode === "command" ? "bg-white text-teal-800" : "text-white/90 hover:text-white"}`}
                    data-testid="homeos-mode-command-btn"
                    aria-pressed={viewMode === "command"}
                  >
                    <LayoutDashboard className="w-3 h-3" strokeWidth={2.5} />
                    Command
                  </button>
                  <button
                    onClick={() => setMode("wall")}
                    className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-extrabold transition-colors ${viewMode === "wall" ? "bg-white text-teal-800" : "text-white/90 hover:text-white"}`}
                    data-testid="homeos-mode-wall-btn"
                    aria-pressed={viewMode === "wall"}
                  >
                    <MonitorPlay className="w-3 h-3" strokeWidth={2.5} />
                    Wall View
                  </button>
                </div>
                <CalmModeStatusPill />
              </div>
            </div>

            {/* Quick capture row */}
            <div className="relative mt-6 flex flex-wrap items-center gap-2">
              <button
                onClick={() => setQuickCaptureOpen(true)}
                className="inline-flex items-center gap-2 bg-white text-teal-800 hover:bg-teal-50 font-extrabold text-sm px-4 py-2.5 rounded-full shadow-md hover:shadow-lg transition-all active:scale-[0.97]"
                data-testid="hero-quick-capture-btn"
              >
                <Mic className="w-4 h-4" strokeWidth={2.5} />
                Quick Capture
                <span className="inline-flex items-center gap-1 ml-1 text-[10px] font-bold text-teal-700 bg-teal-100 px-1.5 py-0.5 rounded-full">
                  voice · photo · type
                </span>
              </button>
              <button
                onClick={() => navigate("/home-os/command-centre")}
                className="inline-flex items-center gap-1.5 bg-white/15 hover:bg-white/25 backdrop-blur-sm border border-white/25 text-white font-bold text-xs px-3.5 py-2 rounded-full"
                data-testid="hero-ai-btn"
              >
                <Sparkles className="w-3.5 h-3.5" strokeWidth={2.5} />
                Ask ARA
              </button>
              {!isCalm && (
                <button
                  onClick={() => setCalmModalOpen(true)}
                  className="inline-flex items-center gap-1.5 bg-white/15 hover:bg-white/25 backdrop-blur-sm border border-white/25 text-white font-bold text-xs px-3.5 py-2 rounded-full"
                  data-testid="calm-mode-header-btn"
                >
                  <Moon className="w-3.5 h-3.5" strokeWidth={2.5} />
                  Calm Mode
                </button>
              )}
              <button
                onClick={() => navigate("/home-os/setup")}
                className="ml-auto inline-flex items-center gap-1 text-[11px] font-bold text-white/80 hover:text-white"
                data-testid="setup-household-btn"
              >
                Set up household <ChevronRight className="w-3 h-3" strokeWidth={3} />
              </button>
            </div>
          </div>
        </div>

        <HomeOSSubNav />

        <div className="px-5 sm:px-8 pb-10 space-y-6 max-w-6xl">

          {/* ════ DAILY FAMILY SCHEDULE — featured top of dashboard ════ */}
          <DailyFamilyScheduleBar />

          {/* ════ MEMORY OF THE DAY — gentle Memories & Media moment ════ */}
          <MemoryOfTheDayCard />

          {/* ════ HOME OS SECTION BENTO — equal-size tiles per OS section ════ */}
          <HomeOSSectionBento sections={osSections} />

          {/* ════ MOOD SNAPSHOT ════ */}
          <section data-testid="section-mood-snapshot">
            <div className="rounded-[28px] border border-rose-100 dark:border-rose-800/40 bg-gradient-to-r from-rose-50 via-amber-50 to-sky-50 dark:from-rose-900/15 dark:via-amber-900/10 dark:to-sky-900/15 p-5 sm:p-6">
              <div className="flex items-start gap-4 flex-wrap">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-rose-400 via-amber-400 to-sky-400 flex items-center justify-center shadow-sm flex-shrink-0">
                  <SmilePlus className="w-6 h-6 text-white" strokeWidth={2.5} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] font-extrabold uppercase tracking-wider text-rose-700 dark:text-rose-300">Family Mood</p>
                  <h2 className="text-xl font-extrabold text-gray-900 dark:text-gray-50 mt-0.5" data-testid="mood-pulse-summary">
                    {moodPulse.summary}
                  </h2>
                  <p className="text-sm font-bold text-gray-600 dark:text-gray-300 mt-1">
                    {moodPulse.checkedIn} of {moodPulse.total} checked in today
                  </p>
                  <div className="mt-3 flex flex-wrap gap-1.5" data-testid="mood-dots">
                    {householdMembers.map(m => {
                      const c = moodByMember.get(m.id);
                      const z = c ? MOOD_ZONES[c.zone] : null;
                      return (
                        <div key={m.id} className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-white/70 dark:bg-slate-800/60 border border-white/40 dark:border-slate-700">
                          <span className={`w-2.5 h-2.5 rounded-full ${z ? z.dot : "bg-gray-300"}`} />
                          <span className="text-xs font-extrabold text-gray-700 dark:text-gray-200">{m.name}</span>
                          {c && <span className={`text-[10px] font-bold ${z.accent}`}>· {c.moodLabel}</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>
                <button
                  onClick={() => navigate("/home-os/mood")}
                  className="inline-flex items-center gap-1.5 bg-rose-500 hover:bg-rose-600 text-white font-extrabold text-sm px-4 py-2.5 rounded-full shadow-md hover:shadow-lg active:scale-[0.97]"
                  data-testid="mood-checkin-btn"
                >
                  <SmilePlus className="w-4 h-4" strokeWidth={2.5} />
                  Mood Check-In
                </button>
              </div>
            </div>
          </section>

          {/* ════ TODAY TIMELINE — category-colored ════ */}
          <section data-testid="section-todays-flow">
            <div className="bg-white dark:bg-slate-800/80 rounded-[28px] border border-gray-100 dark:border-slate-700/60 shadow-sm overflow-hidden">
              <div className="px-6 pt-6 pb-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-amber-200 to-orange-100 dark:from-amber-900/40 dark:to-orange-900/20 flex items-center justify-center">
                    <Sun className="w-5 h-5 text-amber-600 dark:text-amber-300" strokeWidth={2.5} />
                  </div>
                  <div>
                    <h2 className="text-xl font-extrabold text-gray-900 dark:text-gray-50">Today's Family Flow</h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{dateLabel}</p>
                  </div>
                </div>
                <span className="text-xs font-bold text-gray-400" data-testid="timeline-counter">{doneCount}/{items.length} done</span>
              </div>

              <div className="px-6 pb-6">
                <div className="space-y-1">
                  {items.map(item => {
                    const isDone = item.status === "done";
                    const mod = moduleForCategory(item.category);
                    return (
                      <button
                        key={item.id}
                        onClick={() => toggleItem(item.id)}
                        className={`w-full flex items-center gap-4 px-4 py-3.5 rounded-2xl text-left transition-all duration-200 group ${
                          isDone ? "bg-gray-50/60 dark:bg-slate-700/30" : "hover:bg-gray-50 dark:hover:bg-slate-700/40"
                        }`}
                        data-testid={`timeline-item-${item.id}`}
                      >
                        <span className={`text-sm font-bold tabular-nums w-16 flex-shrink-0 ${isDone ? "text-gray-300 dark:text-slate-600" : "text-gray-500 dark:text-gray-400"}`}>
                          {item.time}
                        </span>

                        <div className="flex flex-col items-center flex-shrink-0">
                          <div className={`w-3 h-3 rounded-full border-2 transition-colors ${
                            isDone ? "bg-emerald-500 border-emerald-500" : `${mod.color.dot} border-transparent`
                          }`} />
                        </div>

                        <div className="flex-1 min-w-0 flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <p className={`text-base font-extrabold ${isDone ? "text-gray-400 dark:text-slate-500 line-through" : "text-gray-800 dark:text-gray-100"}`}>
                              {item.title}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 flex items-center gap-1.5">
                              <span className="font-bold">{item.assignedTo}</span>
                              <span className="opacity-50">·</span>
                              <span className={`${mod.color.textAccent} font-extrabold`}>{mod.label}</span>
                            </p>
                          </div>
                          <StatusPill status={item.status} />
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </section>

          {/* Weekly Reset */}
          <WeeklyResetCard onStart={weeklyReset.start} />

          {/* ════ FAMILY ROW ════ */}
          <section data-testid="section-family-members">
            <div className="flex items-center justify-between mb-3 px-1">
              <h2 className="text-xl font-extrabold text-gray-900 dark:text-gray-50">Family</h2>
              <button
                onClick={() => navigate("/home-os/setup")}
                className="inline-flex items-center gap-1 text-[11px] font-extrabold text-pink-600 dark:text-pink-300 hover:underline"
                data-testid="manage-family-link"
              >
                Manage <ChevronRight className="w-3 h-3" strokeWidth={3} />
              </button>
            </div>
            <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide snap-x">
              {householdMembers.map(member => {
                const mc = moodByMember.get(member.id);
                const memberWithMood = mc ? { ...member, _moodCheckin: mc } : member;
                return (
                  <button
                    key={member.id}
                    onClick={() => setEditingMemberId(member.id)}
                    className="text-left rounded-2xl focus:outline-none focus:ring-2 focus:ring-pink-300 dark:focus:ring-pink-600"
                    data-testid={`dashboard-edit-member-${member.id}`}
                    aria-label={`Edit ${member.name}`}
                  >
                    <FamilyMemberCardCompact member={memberWithMood} />
                  </button>
                );
              })}
              <button
                onClick={() => navigate("/home-os/setup")}
                className="snap-start shrink-0 w-[190px] sm:w-[210px] rounded-[24px] border-2 border-dashed border-gray-200 dark:border-slate-700 hover:border-pink-300 dark:hover:border-pink-600 flex flex-col items-center justify-center gap-2 text-gray-400 hover:text-pink-500 transition-colors"
                data-testid="dashboard-add-member"
              >
                <div className="w-11 h-11 rounded-2xl bg-pink-50 dark:bg-pink-900/20 text-pink-500 flex items-center justify-center">
                  <Plus className="w-5 h-5" strokeWidth={2.5} />
                </div>
                <span className="text-xs font-extrabold">Add family member</span>
              </button>
            </div>
          </section>

          {/* ════ HELP OUT — pending swap opportunities ════ */}
          {helpPayload.total > 0 && (
            <section data-testid="section-help-out">
              <div className="rounded-[28px] border-2 border-pink-200 dark:border-pink-800/60 bg-gradient-to-br from-pink-50 via-rose-50 to-amber-50 dark:from-pink-900/15 dark:via-rose-900/10 dark:to-amber-900/10 p-5 sm:p-6">
                <div className="flex items-center gap-3 mb-3 flex-wrap">
                  <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-pink-500 to-rose-500 flex items-center justify-center shadow-sm">
                    <HeartHandshake className="w-5 h-5 text-white" strokeWidth={2.5} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[11px] font-extrabold uppercase tracking-wider text-pink-700 dark:text-pink-300">Help out</p>
                    <h2 className="text-xl font-extrabold text-gray-900 dark:text-gray-50">Tasks available to take</h2>
                    <p className="text-sm font-bold text-gray-600 dark:text-gray-300">
                      {helpPayload.total === 1
                        ? "1 task someone could use backup on."
                        : `${helpPayload.total} tasks someone could use backup on.`}
                    </p>
                  </div>
                  <button
                    onClick={() => navigate("/home-os/chores")}
                    className="ml-auto inline-flex items-center gap-1 text-xs font-extrabold text-pink-700 dark:text-pink-300 bg-white dark:bg-slate-800 border border-pink-200 dark:border-pink-700/60 px-3 py-2 rounded-full hover:bg-pink-50 dark:hover:bg-pink-900/30"
                    data-testid="help-out-view-tasks-btn"
                  >
                    View tasks <ChevronRight className="w-3 h-3" strokeWidth={3} />
                  </button>
                </div>
                <ul className="space-y-2" data-testid="help-out-list">
                  {helpPayload.items.map(it => (
                    <li
                      key={it.id}
                      className="flex items-center gap-3 bg-white/85 dark:bg-slate-800/60 border border-pink-100 dark:border-pink-800/40 rounded-2xl px-3 py-2.5"
                      data-testid={`help-out-item-${it.id}`}
                    >
                      {it.assignee ? (
                        <HomeOSAvatar member={it.assignee} size="sm" showRing={false} />
                      ) : (
                        <span className="w-7 h-7 rounded-full bg-gray-200 dark:bg-slate-700" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-base font-extrabold text-gray-900 dark:text-gray-50 truncate leading-tight">{it.title}</p>
                        <p className="text-[11px] font-bold text-pink-700 dark:text-pink-300 truncate">
                          {it.assignee?.name || "Unassigned"} · {it.categoryOrRoom}
                        </p>
                      </div>
                      <button
                        onClick={() => setSwapTask({ task: it.raw, kind: it.kind })}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-pink-500 hover:bg-pink-600 text-white text-[11px] font-extrabold shadow-sm active:scale-95"
                        data-testid={`help-out-take-${it.id}`}
                      >
                        <HeartHandshake className="w-3 h-3" strokeWidth={2.5} />
                        I'll take this
                      </button>
                    </li>
                  ))}
                </ul>
                <p className="text-[11px] text-gray-500 dark:text-gray-400 italic mt-3">
                  Volunteer for a task without asking. ARA will gently note the kindness.
                </p>
              </div>
            </section>
          )}

          {/* ════ WEEKLY REPORT — compact preview ════ */}
          <section data-testid="section-weekly-report-preview">
            <button
              onClick={() => navigate("/home-os/report")}
              className="w-full text-left rounded-[24px] border border-indigo-200 dark:border-indigo-800/60 bg-gradient-to-r from-indigo-50 via-violet-50 to-pink-50 dark:from-indigo-900/15 dark:via-violet-900/10 dark:to-pink-900/10 p-4 sm:p-5 flex items-center gap-4 flex-wrap hover:shadow-md transition-shadow"
              data-testid="weekly-report-preview-card"
            >
              <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-indigo-500 via-violet-500 to-pink-500 flex items-center justify-center shadow-sm flex-shrink-0">
                <CalendarHeart className="w-5 h-5 text-white" strokeWidth={2.5} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-extrabold uppercase tracking-wider text-indigo-700 dark:text-indigo-300">This Week</p>
                <p className="text-base sm:text-lg font-extrabold text-gray-900 dark:text-gray-50 leading-tight">ARA has a gentle snapshot ready.</p>
                <div className="mt-1.5 flex flex-wrap gap-1.5 text-[11px] font-extrabold" data-testid="weekly-report-preview-stats">
                  <span className="px-2 py-0.5 rounded-full bg-white/80 dark:bg-slate-800/60 text-rose-700 dark:text-rose-300 border border-rose-200 dark:border-rose-800/40">
                    {weeklyPreview.moodCount} mood check-in{weeklyPreview.moodCount === 1 ? "" : "s"}
                  </span>
                  <span className="px-2 py-0.5 rounded-full bg-white/80 dark:bg-slate-800/60 text-cyan-700 dark:text-cyan-300 border border-cyan-200 dark:border-cyan-800/40">
                    {weeklyPreview.openCleaning} open cleaning task{weeklyPreview.openCleaning === 1 ? "" : "s"}
                  </span>
                  <span className="px-2 py-0.5 rounded-full bg-white/80 dark:bg-slate-800/60 text-pink-700 dark:text-pink-300 border border-pink-200 dark:border-pink-800/40">
                    {weeklyPreview.swapCount} helpful swap{weeklyPreview.swapCount === 1 ? "" : "s"}
                  </span>
                </div>
              </div>
              <span
                className="inline-flex items-center gap-1 text-xs font-extrabold text-indigo-700 dark:text-indigo-300 bg-white dark:bg-slate-800 border border-indigo-200 dark:border-indigo-700/60 px-3 py-2 rounded-full flex-shrink-0"
                data-testid="weekly-report-preview-open"
              >
                Open report <ChevronRight className="w-3 h-3" strokeWidth={3} />
              </span>
            </button>
          </section>

          {/* ════ ARA NOTICED — smart, soft, lavender AI accent ════ */}
          <section data-testid="section-ara-noticed">
            <div
              className="rounded-[28px] border border-violet-200/70 dark:border-violet-800/40 p-5 sm:p-6 relative overflow-hidden"
              style={{ background: "linear-gradient(135deg, rgba(237,233,254,0.85) 0%, rgba(243,232,255,0.6) 50%, rgba(224,242,254,0.6) 100%)" }}
            >
              <div className="absolute -top-12 -right-12 w-44 h-44 rounded-full bg-violet-200/40 blur-3xl" aria-hidden />
              <div className="relative flex items-center gap-3 mb-4">
                <div
                  className="w-11 h-11 rounded-2xl flex items-center justify-center shadow-sm"
                  style={{ background: "linear-gradient(135deg, #A78BFA 0%, #8B5CF6 50%, #38BDF8 100%)" }}
                  aria-hidden
                >
                  <Sparkles className="w-5 h-5 text-white" strokeWidth={2.5} />
                </div>
                <div className="min-w-0">
                  <p className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-violet-700 dark:text-violet-300">ARA noticed</p>
                  <h2 className="text-xl font-extrabold text-[var(--whanau-text-primary)] dark:text-gray-50 leading-tight">Smart nudges for today.</h2>
                </div>
              </div>
              <div className="relative grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {araNoticed.map(s => (
                  <div
                    key={s.id}
                    className="flex items-center gap-3 bg-white/80 dark:bg-slate-800/60 rounded-2xl px-3.5 py-3 border border-violet-100/60 dark:border-violet-800/30 hover:shadow-sm transition-shadow"
                    data-testid={`ara-noticed-${s.id}`}
                  >
                    <div className="w-9 h-9 rounded-xl bg-violet-100 dark:bg-violet-900/40 text-violet-600 dark:text-violet-300 flex items-center justify-center flex-shrink-0">
                      <Lightbulb className="w-4 h-4" strokeWidth={2.5} />
                    </div>
                    <p className="text-[14px] font-bold text-[var(--whanau-text-primary)] dark:text-gray-100 leading-snug flex-1 line-clamp-2">{s.text}</p>
                    {s.action && (
                      <button
                        onClick={() => navigate(s.action.route)}
                        className="inline-flex items-center gap-1 text-[11px] font-extrabold text-violet-700 dark:text-violet-300 bg-white dark:bg-slate-800 border border-violet-200 dark:border-violet-700/60 px-2.5 py-1.5 rounded-full hover:bg-violet-50 dark:hover:bg-violet-900/30 flex-shrink-0"
                        data-testid={`ara-noticed-action-${s.id}`}
                      >
                        {s.action.label} <ArrowRightCircle className="w-3 h-3" strokeWidth={2.5} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ════ RENEWAL RADAR (compact) ════ */}
          <section data-testid="section-renewal-radar">
            <RenewalRadar
              variant="compact"
              onOpenDocument={() => navigate("/home-os/documents")}
            />
          </section>

          {/* ════ RENEWAL PREP (ARA started prepping…) ════ */}
          <section data-testid="section-renewal-prep">
            <RenewalPrepCard
              variant="today"
              onOpenDocument={() => navigate("/home-os/documents")}
            />
          </section>

        </div>
      </div>

      <CalmModeModal open={calmModalOpen} onClose={() => setCalmModalOpen(false)} />
      <WeeklyResetModal open={weeklyReset.open} onClose={weeklyReset.close} />
      <FamilyMemberEditModal
        open={!!editingMemberId}
        memberId={editingMemberId}
        mode="edit"
        onClose={() => setEditingMemberId(null)}
      />
      <QuickCaptureModal open={quickCaptureOpen} onClose={() => setQuickCaptureOpen(false)} />
      <SwapTaskModal
        open={!!swapTask}
        task={swapTask?.task || null}
        taskKind={swapTask?.kind || "task"}
        onClose={() => setSwapTask(null)}
      />
      <BetaFeedbackButton />
    </AppLayout>
  );
}
